import { clamp, keyBy, uniqBy } from 'lodash';

//TODO: this is bad, but until these thunks are converted to epics it's necessary
import * as optionGroupAPI from 'domains/option_groups/option_groups.api';
import * as arrangementsAPI from 'domains/saved_arrangements/saved_arrangements.api';

import {
  selectDefaultPPI,
  selectZoomedPPI,
  selectArrangementHeight,
  selectArrangementWidth,
} from 'selectors/ppi';

import subcatalogsModule from 'domains/subcatalogs';
import * as subcatalogsAPI from 'domains/subcatalogs/subcatalogs.api';
import productsModule from 'domains/products';
import optionGroupsModule from 'domains/option_groups';
import photosModule from 'domains/photos';
import dimensionModule from 'domains/stage_dimensions';
import canvasModule from 'domains/canvases';
import savedArrangements from 'domains/saved_arrangements';
import cropsModule from 'domains/crops';

import {
  calculateArrangementCanvasPositions,
  calculateArrangementHeight,
  calculateArrangementWidth,
  findArrangementPositionToCenter,
  getArrangementAvailability,
} from 'utils/arrangement_util';
import { orientationAndRatioPercentage, realNumber } from 'utils/math_util';
import { notNull } from 'utils/type_guards';
import { createRotatedImageBlob, imageFilters } from 'utils/image_util';
import { fetchPhotoBlob } from 'utils/image_load';

import type { AppThunk } from 'store/wos_store';
import type { SavedArrangement } from 'domains/saved_arrangements';
import type { RestoreArrangementCropsPayload } from 'domains/crops';
import type { CanvasConfiguration, NextSizePayload } from 'domains/canvases';
import type { ImageFilter } from 'types/photo_types';
import type { ProductTemplateAPIData } from 'types/catalog_types';
import type { SavedArrangementCanvas } from 'domains/saved_arrangements';

type PrepareArrangementMovePayload = {
  id?: string;
  movementX: number;
  movementY: number;
  nextSize?: NextSizePayload;
};

export function prepareAddCanvas(): AppThunk {
  return function prepareThunk(dispatch, getState) {
    // Place next canvas 1 inch to the right of the arrangement
    const state = getState();
    const arrangementWidth = selectArrangementWidth(state);

    dispatch(canvasModule.actions.setNextCanvasPosition(arrangementWidth + 1));
  };
}

// Converts movement in px to inches, calculates wall preview bounds if needed
export function prepareArrangementMovement(payload: PrepareArrangementMovePayload): AppThunk {
  const { id, nextSize } = payload;
  let { movementX, movementY } = payload;

  return function prepareThunk(dispatch, getState) {
    const state = getState();
    const wallPreviewEnabled = dimensionModule.selectors.wallPreviewActive(state);
    const ppi = selectZoomedPPI(state);

    movementX /= ppi;
    movementY /= ppi;

    // Bound movement within wall preview
    let leftLimit: number | undefined;
    let rightLimit: number | undefined;
    let topLimit: number | undefined;
    let bottomLimit: number | undefined;

    if (wallPreviewEnabled) {
      const wallPreviewBounds = dimensionModule.selectors.wallPreviewBounds(state);

      ({ leftLimit, rightLimit, topLimit, bottomLimit } = wallPreviewBounds);

      // Screen & wall preview dimensions are in px, so adjust to inches
      leftLimit /= ppi;
      topLimit /= ppi;
      rightLimit /= ppi;
      bottomLimit /= ppi;

      // If we have an id, we're moving a canvas. Otherwise, it's the whole arrangement
      if (id) {
        const arrangementPosition = canvasModule.selectors.arrangementPosition(state);
        const canvasConfig = canvasModule.selectors.selectById(state, id);
        if (!canvasConfig || !canvasConfig.product) return;

        const product = productsModule.selectors.selectById(state, canvasConfig.product);
        if (!product) return;

        // If we have next size, we're repositioning based on a product drag-resize
        const productSize =
          nextSize || productsModule.utils.calculateProductSize(product, canvasConfig);

        // Determine bounds for canvas' relative position within the arrangement
        topLimit -= arrangementPosition.y + movementY;
        bottomLimit -= arrangementPosition.y + movementY + productSize.height;
        leftLimit -= arrangementPosition.x + movementX;
        rightLimit -= arrangementPosition.x + movementX + productSize.width;
      } else {
        // Determine the right/bottom bounds based on the entire arrangement size
        const arrangementWidth = selectArrangementWidth(state);
        const arrangementHeight = selectArrangementHeight(state);
        rightLimit -= arrangementWidth;
        bottomLimit -= arrangementHeight;
      }
    }

    const action = id
      ? canvasModule.actions.moveCanvas({
          id,
          movementX,
          movementY,
          leftLimit,
          rightLimit,
          topLimit,
          bottomLimit,
        })
      : canvasModule.actions.moveArrangement({
          movementX,
          movementY,
          leftLimit,
          rightLimit,
          topLimit,
          bottomLimit,
        });

    dispatch(action);
  };
}

// Calculates the correct zoom value to fit everything within the screen
export function calculateAutoFit(shouldCenterArrangement?: boolean): AppThunk {
  return function autoFitThunk(dispatch, getState) {
    const state = getState();
    const defaultPPI = selectDefaultPPI(state);

    // Unlikely we would be running this if default ppi is not ready,
    // but since we're dividing by it we should break early if that's the case
    if (!defaultPPI) return;

    const arrangementDimensions = {
      width: selectArrangementWidth(state),
      height: selectArrangementHeight(state),
    };
    const screenDimensions = dimensionModule.selectors.screenDimensions(state);

    // Find the zoom value to get a ppi to fit the arrangement on the screen
    const { orientation } = orientationAndRatioPercentage(
      arrangementDimensions.width,
      arrangementDimensions.height
    );

    const primaryDim = orientation === 'landscape' ? 'width' : 'height';
    const secondaryDim = primaryDim === 'width' ? 'height' : 'width';

    let newppi = screenDimensions[primaryDim] / arrangementDimensions[primaryDim];

    // If the calculated primary dimension is going to be too large, use the other one
    if (arrangementDimensions[secondaryDim] * newppi > screenDimensions[secondaryDim]) {
      newppi = screenDimensions[secondaryDim] / arrangementDimensions[secondaryDim];
    }

    // Find the zoom value to fit items on stage, adjust for a little extra margin
    let fitZoom = newppi / defaultPPI;
    fitZoom -= fitZoom * 0.1;
    dispatch(dimensionModule.actions.setStageZoom(fitZoom));

    if (shouldCenterArrangement) {
      dispatch(centerArrangement());
    }
  };
}

// Sets the arrangement position to center it within the stage dimensions
export function centerArrangement(): AppThunk {
  return function centerArrangementThunk(dispatch, getState) {
    const state = getState();

    const ppi = selectZoomedPPI(state);
    const arrangementWidth = selectArrangementWidth(state);
    const arrangementHeight = selectArrangementHeight(state);
    const { width: screenWidth, height: screenHeight } =
      dimensionModule.selectors.screenDimensions(state);

    // We only want to center if it is _explicitly_ not centered
    // If we don't have the data for it, skip any render changes
    if (!ppi || !arrangementWidth || !arrangementHeight || !screenHeight || !screenWidth) return;

    const currentPosition = canvasModule.selectors.arrangementPosition(state);

    const centerPosition = findArrangementPositionToCenter(
      { width: arrangementWidth, height: arrangementHeight },
      { width: screenWidth / ppi, height: screenHeight / ppi }
    );

    // If already centered, skip any changes
    if (currentPosition.x === centerPosition.x && currentPosition.y === centerPosition.y) {
      return;
    }

    dispatch(
      canvasModule.actions.setArrangementPosition({ x: centerPosition.x, y: centerPosition.y })
    );
  };
}

// Clamps the arrangement position within the bounds of the wall preview size
export function wallPreviewBoundsCheck(): AppThunk {
  return function boundsCheckThunk(dispatch, getState) {
    const state = getState();

    const ppi = selectZoomedPPI(state);
    const arrangementPosition = canvasModule.selectors.arrangementPosition(state);
    const arrangementWidth = selectArrangementWidth(state);
    const arrangementHeight = selectArrangementHeight(state);
    let { leftLimit, rightLimit, topLimit, bottomLimit } =
      dimensionModule.selectors.wallPreviewBounds(state);

    // Skip if limits are not defined yet
    if (!realNumber(leftLimit)) return;

    leftLimit /= ppi;
    topLimit /= ppi;
    rightLimit = rightLimit / ppi - arrangementWidth;
    bottomLimit = bottomLimit / ppi - arrangementHeight;

    const arrangementRightEdge = arrangementPosition.x + arrangementWidth;
    const arrangementBottomEdge = arrangementPosition.y + arrangementHeight;

    const withinLeftBound = arrangementPosition.x > leftLimit;
    const withinRightBound = arrangementRightEdge < rightLimit;
    const withinTopBound = arrangementPosition.y > topLimit;
    const withinBottomBound = arrangementBottomEdge < bottomLimit;

    if (!withinRightBound || !withinLeftBound || !withinTopBound || !withinBottomBound) {
      const x = clamp(arrangementPosition.x, leftLimit, rightLimit);
      const y = clamp(arrangementPosition.y, topLimit, bottomLimit);

      dispatch(canvasModule.actions.setArrangementPosition({ x, y }));
    }
  };
}

export function prepareRestoreArrangement(arrangement: SavedArrangement): AppThunk {
  return async function prepareRestoreArrangementThunk(dispatch, getState) {
    let preState = getState();

    // Arrangements can only be composed of subcatalogs within one catalog
    // So, if we have the subcatalog of the first canvas, we will have all the
    // possible subcatalogs in this arrangement
    const firstCanvasConfig = arrangement.canvases[0].config;
    const subcatalog = subcatalogsModule.selectors.selectById(
      preState,
      firstCanvasConfig.subcatalog
    );

    // If we don't have the subcatalogs for this arrangement, fetch and add them to the store
    if (!subcatalog) {
      const catalogId = firstCanvasConfig.catalog;
      const response = await subcatalogsAPI.allByCatalogId(catalogId);
      dispatch(
        subcatalogsModule.actions.fetchSubcatalogs.fulfilled({
          subcatalogs: response,
          catalogId,
        })
      );
    }

    // We have the subcatalogs & products for sure now, let's check for option groups
    preState = getState();
    const uniqueProductCanvases = uniqBy(arrangement.canvases, (canvas) => canvas.config.product);
    const haveAllOptionGroups = uniqueProductCanvases.every((canvas) => {
      const {
        config: { product },
      } = canvas;
      const optionGroups = optionGroupsModule.selectors.forTemplateId(preState, product);
      return optionGroups && optionGroups.length > 0;
    });

    if (!haveAllOptionGroups) {
      const promises = uniqueProductCanvases.map((canvas) => {
        const {
          config: { product },
        } = canvas;
        return optionGroupAPI.allByTemplateId(product);
      });

      const allOptionGroupsAndOptions = await Promise.all(promises);

      allOptionGroupsAndOptions.forEach((joined, index) => {
        dispatch(
          optionGroupsModule.actions.fetchOptionGroups.fulfilled({
            optionGroups: joined,
            templateId: uniqueProductCanvases[index].config.product,
          })
        );
      });
    }

    preState = getState();
    const unavailable = getArrangementAvailability(preState, arrangement.canvases);

    if (unavailable.length > 0) {
      dispatch(
        savedArrangements.actions.addUnavailableArrangements({
          id: arrangement.id,
          items: unavailable,
        })
      );
    } else {
      dispatch(restoreArrangementAndAutoFit(arrangement));
    }

    // TODO: Also need to dispatch fetch svgs if there are any
  };
}

export function restoreArrangementAndAutoFit(arrangement: SavedArrangement): AppThunk {
  return async function restoreArrangementThunk(dispatch, getState) {
    const state = getState();
    let { crops } = arrangement;

    // Key crops by canvas ID to make later merging easy
    const cropsByCanvasId = keyBy(crops, 'canvas_id');

    dispatch(canvasModule.actions.restoreArrangement(arrangement));

    // Match up photos with crops, and a promise for the crop's base image blob
    const photosAndCrops = crops
      .map((crop) => {
        const photo = photosModule.selectors.selectById(state, crop.ordering_upload_id);
        if (!photo) return null;

        return { crop, photo };
      })
      .filter(notNull)
      .map((merged) => {
        const { crop, photo } = merged;
        const appliedFilter = crop.filter_applied || 'original';
        const filter = imageFilters.find((f) => f.filterType === appliedFilter) as ImageFilter;
        const imageSrc = photo[filter.filePathFull];

        return { ...merged, blobPromise: fetchPhotoBlob(imageSrc) };
      });

    // Fetch all the base blobs
    const blobs = await Promise.all(photosAndCrops.map((merged) => merged.blobPromise));

    // Create promises for rotated crop image blobs
    const withRotationPromises = blobs.map((blob, index) => {
      // index will match the merged photosAndCrops array
      const matched = photosAndCrops[index];

      // If we don't need to rotate it, Promise.all will resolve with the original blob
      if (matched.crop.degrees_rotated === 0) return blob;

      const promise = createRotatedImageBlob(blob, matched.crop.degrees_rotated);
      return promise;
    });

    // Fetch all the rotated blobs, resolving the unrotated ones with the original
    const withRotatedBlobs = await Promise.all(withRotationPromises);

    // Merge the crop with the blob src
    const restoreCrops: RestoreArrangementCropsPayload[] = withRotatedBlobs.map((blob, index) => {
      // Again, index will match the merged photosAndCrops array
      const { canvas_id } = photosAndCrops[index].crop;
      const blobSrc = URL.createObjectURL(blob);

      return { ...cropsByCanvasId[canvas_id], blobSrc };
    });

    dispatch(cropsModule.actions.restoreArrangementCrops(restoreCrops));

    const wallPreviewEnabled = dimensionModule.selectors.wallPreviewActive(state);
    if (wallPreviewEnabled) {
      dispatch(centerArrangement());
    } else {
      dispatch(calculateAutoFit(true));
    }
  };
}

export function updateUnavailableArrangement(
  removeIds: string[],
  arrangement: SavedArrangement
): AppThunk {
  return async function updateUnavailableArrangementThunk(dispatch, getState) {
    let state = getState();
    const productsById = productsModule.selectors.selectEntities(state);

    // We have to do several different modifications based on the arrangement passed in,
    // so just do them all in one loop
    const canvasesForSave: Record<string, SavedArrangementCanvas> = {};
    const canvasesById: Record<string, CanvasConfiguration> = {};
    let canvasesWithArrangementShape: Pick<CanvasConfiguration, 'position' | 'id'>[] = [];
    arrangement.canvases.forEach((canvas) => {
      const { x, y, width, height, config } = canvas;
      const { id } = config;

      // Exclude the removed ones
      if (removeIds.indexOf(id) === -1) {
        canvasesById[id] = config;
        canvasesForSave[id] = { config, width, height, x: 0, y: 0 };
        canvasesWithArrangementShape.push({ position: { x, y }, id });
      }
    });

    const repositionedCanvases = calculateArrangementCanvasPositions(canvasesWithArrangementShape);

    // Put them back to the right shape after adjusting the positions
    // and add the positions to the array for PUT request
    canvasesWithArrangementShape = [];
    repositionedCanvases.updates.forEach((canvas) => {
      const { changes, id } = canvas;
      const { x, y } = changes.position;

      canvasesForSave[id].x = x;
      canvasesForSave[id].y = y;
      canvasesWithArrangementShape.push({ position: { x, y }, id });
    });

    const width = calculateArrangementWidth(
      canvasesWithArrangementShape,
      canvasesById,
      productsById as Record<number, ProductTemplateAPIData>
    );
    const height = calculateArrangementHeight(
      canvasesWithArrangementShape,
      canvasesById,
      productsById as Record<number, ProductTemplateAPIData>
    );

    const keepCrops = arrangement.crops.filter((crop) => {
      return !!canvasesForSave[crop.canvas_id];
    });

    // Save the arrangement with updated canvases & size
    const resp = await arrangementsAPI.update(arrangement.id, {
      name: arrangement.name,
      width,
      height,
      canvases: Object.values(canvasesForSave),
      crops: keepCrops,
    });

    // Send the update response to the reducer
    dispatch(savedArrangements.actions.update.fulfilled(resp));

    // Get the arrangement after putting it through reducer which normalizes the data
    state = getState();
    const toRestore = savedArrangements.selectors.selectById(
      state,
      arrangement.id
    ) as SavedArrangement;
    dispatch(restoreArrangementAndAutoFit(toRestore));
  };
}
